import React, { useState, useEffect } from 'react';
import { Popup, Message, Button, Form, Icon } from 'semantic';

import Panel from '../Panel/Panel';
import { request, joiErrorDetailsToObject } from 'utils/api';
import { Formik } from 'formik';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useUser } from 'contexts/user';
import { getRequiredFieldMessage } from 'utils/validate';
import * as Yup from 'yup';

import RequiredIndicator from 'components/RequiredIndicator';
import InputField from 'components/form-fields/formik/InputField';
import CheckboxField from 'components/form-fields/formik/CheckboxField';
import LegalLink from 'components/LegalLink';
import PhoneCountry from 'components/form-fields/formik/PhoneCountry';
import PhoneNumber from 'components/form-fields/formik/PhoneNumber';
import LayoutContainer from '../LayoutContainer';
import { User } from 'types/user';

import { parseToken } from 'utils/token';

interface QueryParams {
  token?: string;
  redirect?: string;
  [key: string]: string | undefined;
}

interface LocationLike {
  search: string;
  pathname: string;
}

function parseLocationParams(
  location: LocationLike,
  fields: string[]
): QueryParams {
  const searchParams = new URLSearchParams(location.search);

  const result: QueryParams = {};

  // Extract specific fields if provided
  if (fields.length > 0) {
    fields.forEach((field) => {
      const value = searchParams.get(field);
      if (value) result[field] = value;
    });
  }

  // Handle redirect parameter processing
  if (result.redirect) {
    result.redirect = result.redirect.replace('://', '');
  }

  return result;
}

interface IAcceptInviteForm {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  phoneNo: string;
  phoneCountryCode: string;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
}

export default function AcceptInvite({ location }: { location: LocationLike }) {
  const { token, redirect } = parseLocationParams(location, [
    'token',
    'redirect',
  ]);
  const [error, setError] = useState<{ status: number; message: string }>();

  const params = new URLSearchParams(location.search);
  const isCompact = params.get('mode') === 'compact';
  const [jwt] = useState(parseToken(params.get('token')));

  // note this is very partial user.
  const [userInformation, setUserInformation] = useState<User>();

  const { provider, setToken } = useUser();

  const isCreateUser = location.pathname.includes(
    '/invites/accept/create-user'
  );

  // If the user is new or existing we use different endpoints
  const path = isCreateUser
    ? '/1/invites/accept/create-user'
    : '/1/auth/accept-invite';

  const { i18n, t } = useTranslation();

  async function fetchUserInfo(token: string) {
    try {
      const { data } = await request({
        method: 'GET',
        path: '/1/invites/user-information',
        token,
      });
      if (data.defaultLanguage) {
        i18n.changeLanguage(data.defaultLanguage);
      }
      if (data) {
        setUserInformation(data);
      }
    } catch (error: any) {
      setUserInformation({} as User);
      setError(error);
    }
  }

  useEffect(() => {
    if (token && !isCreateUser) {
      // this only works for existing users
      fetchUserInfo(token);
    } else if (jwt?.firstName) {
      setUserInformation({
        email: jwt.email || '',
        contact: {
          firstName: jwt.firstName || '',
          lastName: jwt.lastName || '',
        },
      } as any);
    }
  }, [token, isCreateUser, jwt]);

  useEffect(() => {
    jwt && jwt.lng && i18n.changeLanguage(jwt.lng);
  }, [jwt]);

  async function onSubmit(formValues: IAcceptInviteForm, formikBag: any) {
    try {
      const { data } = await request({
        method: 'POST',
        path: path,
        body: {
          platform: 'web',
          language: i18n.language,
          ...formValues,
          token,
        },
      });
      setToken(data.token);
      window.location.href = redirect || '/';
    } catch (error: any) {
      if (Array.isArray(error?.details)) {
        formikBag.setErrors(joiErrorDetailsToObject(error));
      }
      formikBag.setStatus(error?.message);
    }
  }

  const labels = {
    firstName: t('formLabel.firstName', 'First Name'),
    lastName: t('formLabel.lastName', 'Last Name'),
    password: t('formLabel.password', 'Password'),
    phoneNo: t('formLabel.phoneNo', 'Phone Number'),
    phoneCountryCode: t('formLabel.phoneCountryCode', 'Extension'),
    acceptedTerms: t('formLabel.acceptedTerms', 'Terms of Service'),
    acceptedPrivacy: t('formLabel.acceptedPrivacy', 'Privacy Statement'),
  };

  const initialValues: IAcceptInviteForm = {
    phoneNo: '',
    phoneCountryCode: '',
    firstName: '',
    lastName: '',
    ...(userInformation?.contact || {}),
    password: '',
    confirmPassword: '',
    acceptedTerms: false,
    acceptedPrivacy: false,
  };

  if (error?.status === 401) {
    return (
      <LayoutContainer>
        <div style={{ maxWidth: '600px', margin: 'auto' }}>
          <Panel
            headerText={t('error.noValidToken', 'No valid token found')}
            content={
              <Message
                error
                content={t(
                  'error.noValidTokenDescription',
                  'The provided token is invalid or has expired.'
                )}
              />
            }
          />
        </div>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <Panel
          headerText={
            isCreateUser
              ? t('signupAcceptInvite.acceptInvite', 'Accept Invite')
              : t(
                  'signupAcceptInvite.completeYourAccount',
                  'Complete your account'
                )
          }
          subHeaderText={
            isCreateUser
              ? t(
                  'signupAcceptInvite.acceptInviteFinalize',
                  'Finalize your account to accept the invite'
                )
              : t(
                  'signupAcceptInvite.fillInDetails',
                  'Fill in the details below to complete your account'
                )
          }
          content={
            <Formik
              validationSchema={Yup.object({
                firstName: Yup.string().required(
                  getRequiredFieldMessage(t, labels.firstName)
                ),
                lastName: Yup.string().required(
                  getRequiredFieldMessage(t, labels.lastName)
                ),
                password: Yup.string().required(
                  t('email.passwordIsRequired', 'Password is required')
                ),
                confirmPassword: Yup.string()
                  .required(
                    t('signup.confirmPassword', 'Please re-enter your password')
                  )
                  .oneOf(
                    [Yup.ref<string>('password')],
                    t('signup.passwordsDoNotMatch', 'Passwords do not match')
                  ),
                phoneNo: Yup.string().required(
                  getRequiredFieldMessage(t, labels.phoneNo)
                ),
                phoneCountryCode: Yup.string()
                  .length(
                    2,
                    getRequiredFieldMessage(t, labels.phoneCountryCode)
                  )
                  .required(
                    getRequiredFieldMessage(t, labels.phoneCountryCode)
                  ),
                acceptedTerms: Yup.boolean().oneOf(
                  [true],
                  getRequiredFieldMessage(t, labels.acceptedTerms)
                ),
                acceptedPrivacy: Yup.boolean().oneOf(
                  [true],
                  getRequiredFieldMessage(t, labels.acceptedPrivacy)
                ),
              })}
              // because we show the form with initial values before we fetch the user information
              enableReinitialize
              initialValues={initialValues}
              onSubmit={onSubmit}>
              {({ isSubmitting, handleSubmit, status }) => {
                return (
                  <Form onSubmit={handleSubmit}>
                    {userInformation?.email && (
                      <Form.Input
                        required
                        disabled
                        name="email"
                        label={t('formLabel.email', 'Email')}
                        value={userInformation.email}
                      />
                    )}
                    {(!initialValues.firstName ||
                      !initialValues.lastName ||
                      !isCompact) && (
                      <Form.Group widths="equal">
                        <InputField
                          required
                          autoComplete="firstName"
                          label={labels.firstName}
                          type="text"
                          name="firstName"
                        />

                        <InputField
                          required
                          autoComplete="lastname"
                          name="lastName"
                          label={labels.lastName}
                          type="text"
                        />
                      </Form.Group>
                    )}

                    {(!initialValues.phoneCountryCode ||
                      !initialValues.phoneNo ||
                      !isCompact) && (
                      <>
                        <div
                          className="required field"
                          style={{ marginTop: '1em', margin: 0 }}>
                          <label>{labels.phoneNo}</label>
                        </div>
                        <Form.Field>
                          <Form.Group widths="equal">
                            <PhoneCountry name="phoneCountryCode" />
                            <PhoneNumber required name="phoneNo" />
                          </Form.Group>
                        </Form.Field>
                      </>
                    )}

                    <Form.Field required>
                      <Popup
                        content={t(
                          'signup.passwordRequirements',
                          'Password must contain at least 8 characters, with one letter, one number and one special character'
                        )}
                        trigger={
                          <label>
                            {t('formLabel.password', 'Password')}{' '}
                            <Icon
                              styles={{
                                display: 'block',
                              }}
                              name="circle-info"
                              color="grey"
                            />
                          </label>
                        }
                      />

                      <InputField
                        name="password"
                        autoComplete="new-password"
                        required
                        type={'password'}
                      />
                    </Form.Field>

                    <Form.Field>
                      <InputField
                        name="confirmPassword"
                        required
                        type={'password'}
                        label={t(
                          'formLabel.confirmPassword',
                          'Confirm Password'
                        )}
                      />
                    </Form.Field>

                    <CheckboxField
                      name="acceptedTerms"
                      required
                      label={
                        <label>
                          <Trans i18nKey="signupAccount.acceptTermsOfServices">
                            I accept the&nbsp;
                            <LegalLink type="tos">Terms of Service</LegalLink>.
                          </Trans>
                        </label>
                      }
                    />

                    <CheckboxField
                      name="acceptedPrivacy"
                      hideErrorLabel
                      label={
                        <label>
                          <span
                            style={{
                              display: 'inline-block',
                              maxWidth: '600px',
                              lineHeight: '1.5em',
                              transform: 'translateY(-2px)',
                            }}>
                            <Trans
                              i18nKey="signupAccount.acceptPrivacy"
                              platformName={provider?.platformName}>
                              I understand that{' '}
                              {{ platformName: provider?.platformName }} will
                              process my personal data in accordance with the
                              <LegalLink type="privacy">
                                Privacy Statement
                              </LegalLink>
                              .
                            </Trans>{' '}
                            <RequiredIndicator />
                          </span>
                        </label>
                      }
                    />

                    {status && <Message error content={status} />}

                    <Button
                      style={{ margin: 0 }}
                      type="submit"
                      fluid
                      primary
                      size="large"
                      content={
                        isCreateUser
                          ? t(
                              'signupAcceptInvite.createAccount',
                              'Create Account'
                            )
                          : t('signupAcceptInvite.continue', 'Continue')
                      }
                      loading={isSubmitting}
                    />
                  </Form>
                );
              }}
            </Formik>
          }
          navigateDirection="none"
          isLoading={!isCreateUser && !userInformation}
          errorMessage={error?.message}
        />
      </div>
    </LayoutContainer>
  );
}
